import React, { useState, useEffect, useRef } from 'react';
import { Filter, Download, FileDown, RefreshCw, ChevronLeft, ChevronRight, ExternalLink, Calendar, User } from 'lucide-react';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, RadialLinearScale, Title, Tooltip, Legend } from 'chart.js';
import { Doughnut, Bar, Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import './Dashboard.css';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const Dashboard = ({ posts, fetchPosts }) => {
  const [filters, setFilters] = useState({
    q: '',
    category: '',
    status: '',
    institution: '',
    author: '',
    dateFrom: '',
    dateTo: ''
  });
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const dashboardRef = useRef(null);

  useEffect(() => {
    applyFilters();
  }, [posts]);

  const applyFilters = () => {
    let result = [...posts];

    if (filters.q) {
      const q = filters.q.toLowerCase();
      result = result.filter(p => 
        (p.author || '').toLowerCase().includes(q) ||
        (p.mentioned || '').toLowerCase().includes(q) ||
        (p.desc || '').toLowerCase().includes(q) ||
        (p.link || '').toLowerCase().includes(q)
      );
    }

    if (filters.category) {
      result = result.filter(p => p.category === filters.category);
    }

    if (filters.status) {
      result = result.filter(p => p.Estado_Actual === filters.status);
    }

    if (filters.institution) {
      const inst = filters.institution.toLowerCase();
      result = result.filter(p => (p.mentioned || '').toLowerCase().includes(inst));
    }

    if (filters.author) {
      const auth = filters.author.toLowerCase();
      result = result.filter(p => (p.author || '').toLowerCase().includes(auth));
    }

    if (filters.dateFrom) {
      result = result.filter(p => (p.date || '').slice(0, 10) >= filters.dateFrom);
    }

    if (filters.dateTo) {
      result = result.filter(p => (p.date || '').slice(0, 10) <= filters.dateTo);
    }

    // Ordenar por fecha descendente
    result.sort((a, b) => new Date(b.date) - new Date(a.date));

    setFilteredPosts(result);
  };

  // Funciones del carrusel
  const nextSlide = () => {
    setCarouselIndex(prev => (prev + 1) % Math.ceil(filteredPosts.length / 3));
  };

  const prevSlide = () => {
    setCarouselIndex(prev => prev === 0 ? Math.ceil(filteredPosts.length / 3) - 1 : prev - 1);
  };

  const getCarouselPosts = () => {
    const postsPerSlide = 3;
    const start = carouselIndex * postsPerSlide;
    return filteredPosts.slice(start, start + postsPerSlide);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      q: '',
      category: '',
      status: '',
      institution: '',
      author: '',
      dateFrom: '',
      dateTo: ''
    });
    setFilteredPosts(posts);
  };

  const setQuickFilter = (days) => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - days);
    
    setFilters(prev => ({
      ...prev,
      dateFrom: from.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0]
    }));
  };

  const setMonthFilter = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    
    setFilters(prev => ({
      ...prev,
      dateFrom: firstDay.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0]
    }));
  };

  const setYearFilter = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), 0, 1);
    
    setFilters(prev => ({
      ...prev,
      dateFrom: firstDay.toISOString().split('T')[0],
      dateTo: today.toISOString().split('T')[0]
    }));
  };

  // Preparar datos para gráficos
  const getChartData = () => {
    const colors = [
      '#1E88E5', '#60A5FA', '#7DD3FC', '#10B981', '#F59E0B',
      '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'
    ];

    // Por categoría
    const categoryCounts = {};
    filteredPosts.forEach(p => {
      const cat = p.category || 'Sin categoría';
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    });

    const categoryData = {
      labels: Object.keys(categoryCounts),
      datasets: [{
        data: Object.values(categoryCounts),
        backgroundColor: colors.slice(0, Object.keys(categoryCounts).length),
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    // Por autor (top 10)
    const authorCounts = {};
    filteredPosts.forEach(p => {
      const auth = p.author || 'Desconocido';
      authorCounts[auth] = (authorCounts[auth] || 0) + 1;
    });
    const authorSorted = Object.entries(authorCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    const authorData = {
      labels: authorSorted.map(([k]) => k),
      datasets: [{
        label: 'Publicaciones',
        data: authorSorted.map(([, v]) => v),
        backgroundColor: colors.slice(0, authorSorted.length),
        borderWidth: 1,
        borderColor: '#fff'
      }]
    };

    // Por institución (top 10)
    const instCounts = {};
    filteredPosts.forEach(p => {
      const inst = p.mentioned || 'Sin institución';
      instCounts[inst] = (instCounts[inst] || 0) + 1;
    });
    const instSorted = Object.entries(instCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    const instData = {
      labels: instSorted.map(([k]) => k),
      datasets: [{
        label: 'Publicaciones',
        data: instSorted.map(([, v]) => v),
        backgroundColor: colors.slice(0, instSorted.length),
        borderWidth: 1,
        borderColor: '#fff'
      }]
    };

    // Por red social
    const netCounts = {};
    filteredPosts.forEach(p => {
      const net = p.network || 'Otra';
      netCounts[net] = (netCounts[net] || 0) + 1;
    });

    const netData = {
      labels: Object.keys(netCounts),
      datasets: [{
        data: Object.values(netCounts),
        backgroundColor: colors.slice(0, Object.keys(netCounts).length),
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    return { categoryData, authorData, instData, netData };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 12,
          font: { size: 11, weight: '600' },
          color: '#0F172A'
        }
      },
      datalabels: {
        color: '#fff',
        font: { size: 11, weight: 'bold' },
        formatter: (value) => value > 0 ? value : ''
      }
    }
  };

  const barOptions = {
    ...chartOptions,
    indexAxis: 'y',
    scales: {
      x: {
        ticks: { color: '#5B6B82', font: { size: 11 } },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      y: {
        ticks: { color: '#5B6B82', font: { size: 11 } },
        grid: { display: false }
      }
    }
  };

  const barOptions2 = {
    ...chartOptions,
    scales: {
      x: {
        ticks: { color: '#5B6B82', font: { size: 11 } },
        grid: { display: false }
      },
      y: {
        ticks: { color: '#5B6B82', font: { size: 11 } },
        grid: { color: 'rgba(0,0,0,0.05)' }
      }
    }
  };

  const { categoryData, authorData, instData, netData } = getChartData();

  const exportToExcel = () => {
    try {
      const data = filteredPosts.map(p => ({
        ID: p.id,
        Fecha: (p.date || '').slice(0, 10),
        Autor: p.author,
        Red: p.network,
        Institución: p.mentioned,
        Personas: p.persons,
        Categoría: p.category,
        Estado: p.Estado_Actual,
        Descripción: p.desc,
        Enlace: p.link
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Dashboard');
      XLSX.writeFile(wb, `dashboard_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      toast.error('Error al exportar Excel');
    }
  };

  const exportToPDF = async () => {
    try {
      toast.info('Generando PDF del tablero...');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      
      // Header con fondo azul
      pdf.setFillColor(30, 136, 229);
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.text('Monitoreo de Medios', pageWidth / 2, 15, { align: 'center' });
      
      pdf.setFontSize(13);
      pdf.text('Tablero de Indicadores', pageWidth / 2, 25, { align: 'center' });
      
      let yPos = 42;
      
      // Información de generación
      pdf.setTextColor(80, 80, 80);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      const dateStr = new Date().toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      pdf.text(`Fecha de generación: ${dateStr}`, margin, yPos);
      pdf.text(`Total de registros: ${filteredPosts.length}`, pageWidth - margin - 50, yPos);
      yPos += 10;
      
      // Capturar cada gráfico individualmente
      const chartsContainer = dashboardRef.current;
      const chartCards = chartsContainer.querySelectorAll('.chart-card');
      
      for (let i = 0; i < chartCards.length; i++) {
        const chartCard = chartCards[i];
        
        // Capturar el gráfico
        const canvas = await html2canvas(chartCard, {
          scale: 1.5,
          logging: false,
          useCORS: true,
          backgroundColor: '#FFFFFF'
        });
        
        const imgData = canvas.toDataURL('image/png', 0.8);
        const imgWidth = (pageWidth - (margin * 2)) / 2 - 5;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Calcular posición (2 columnas)
        const col = i % 2;
        const xPos = margin + (col * (imgWidth + 10));
        
        // Si necesitamos nueva página
        if (yPos + imgHeight > pageHeight - 20) {
          pdf.addPage();
          yPos = 20;
        }
        
        pdf.addImage(imgData, 'PNG', xPos, yPos, imgWidth, imgHeight);
        
        // Si es impar (segunda columna), bajamos para la siguiente fila
        if (col === 1) {
          yPos += imgHeight + 10;
        }
      }
      
      // Footer en cada página
      const totalPages = pdf.internal.pages.length - 1;
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setTextColor(150, 150, 150);
        pdf.setFontSize(8);
        pdf.text(
          `Sistema de Monitoreo de Medios JAUM | Página ${i} de ${totalPages}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }
      
      pdf.save(`monitoreo_medios_tablero_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF del tablero generado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      toast.error('Error al generar PDF: ' + error.message);
    }
  };

  return (
    <div className="dashboard-container fade-in" data-testid="dashboard-view">
      <div className="container">
        {/* Filtros */}
        <div className="panel" data-testid="filters-panel">
          <div className="panel-header">
            <h2 className="panel-title">
              <Filter size={20} />
              Filtros de Búsqueda
            </h2>
          </div>

          <div className="filters-grid">
            <div className="filter-item">
              <label>Búsqueda general</label>
              <input
                type="text"
                data-testid="filter-search"
                placeholder="Autor, institución, descripción..."
                value={filters.q}
                onChange={(e) => handleFilterChange('q', e.target.value)}
              />
            </div>

            <div className="filter-item">
              <label>Categoría</label>
              <select
                data-testid="filter-category"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">Todas</option>
                <option value="ATENDER">ATENDER</option>
                <option value="SEGUIMIENTO">SEGUIMIENTO</option>
                <option value="MONITOREO">MONITOREO</option>
              </select>
            </div>

            <div className="filter-item">
              <label>Estado</label>
              <select
                data-testid="filter-status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="Registrado">Registrado</option>
                <option value="Enviado/Compartido">Enviado/Compartido</option>
                <option value="En proceso averiguación">En proceso averiguación</option>
                <option value="Archivado">Archivado</option>
              </select>
            </div>

            <div className="filter-item">
              <label>Institución</label>
              <input
                type="text"
                data-testid="filter-institution"
                placeholder="Ej. Ministerio de Cultura"
                value={filters.institution}
                onChange={(e) => handleFilterChange('institution', e.target.value)}
              />
            </div>

            <div className="filter-item">
              <label>Autor</label>
              <input
                type="text"
                data-testid="filter-author"
                placeholder="@usuario"
                value={filters.author}
                onChange={(e) => handleFilterChange('author', e.target.value)}
              />
            </div>

            <div className="filter-item">
              <label>Desde</label>
              <input
                type="date"
                data-testid="filter-date-from"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>

            <div className="filter-item">
              <label>Hasta</label>
              <input
                type="date"
                data-testid="filter-date-to"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>

          <div className="quick-filters">
            <div className="quick-filters-label">Filtros rápidos:</div>
            <div className="quick-filters-buttons">
              <button className="btn-quick" onClick={() => setQuickFilter(1)} data-testid="filter-24h">
                Últimas 24h
              </button>
              <button className="btn-quick" onClick={() => setQuickFilter(7)} data-testid="filter-7d">
                Últimos 7 días
              </button>
              <button className="btn-quick" onClick={() => setQuickFilter(30)} data-testid="filter-30d">
                Últimos 30 días
              </button>
              <button className="btn-quick" onClick={setMonthFilter} data-testid="filter-month">
                Mes actual
              </button>
              <button className="btn-quick" onClick={setYearFilter} data-testid="filter-year">
                Año actual
              </button>
            </div>
          </div>

          <div className="filters-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters} data-testid="clear-filters-btn">
              Limpiar
            </button>
            <button className="btn btn-primary" onClick={applyFilters} data-testid="apply-filters-btn">
              Aplicar Filtros
            </button>
          </div>
        </div>

        {/* Stats & Actions */}
        <div className="panel stats-panel">
          <div className="stats-content">
            <div className="stat-item">
              <div className="stat-label">Total de registros</div>
              <div className="stat-value" data-testid="total-count">{filteredPosts.length}</div>
            </div>
          </div>
          <div className="stats-actions">
            <button className="btn btn-secondary btn-sm" onClick={fetchPosts} data-testid="refresh-btn">
              <RefreshCw size={16} />
              Actualizar
            </button>
            <button className="btn btn-secondary btn-sm" onClick={exportToExcel} data-testid="export-excel-btn">
              <FileDown size={16} />
              Excel
            </button>
            <button className="btn btn-primary btn-sm" onClick={exportToPDF} data-testid="export-pdf-btn">
              <Download size={16} />
              PDF Tablero
            </button>
          </div>
        </div>

        {/* Carrusel de Publicaciones Recientes */}
        {filteredPosts.length > 0 && (
          <div className="panel carousel-panel">
            <div className="panel-header">
              <h2 className="panel-title">Publicaciones Recientes</h2>
              <div className="carousel-controls">
                <button 
                  className="carousel-btn" 
                  onClick={prevSlide}
                  disabled={filteredPosts.length <= 3}
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="carousel-indicator">
                  {carouselIndex + 1} / {Math.ceil(filteredPosts.length / 3)}
                </span>
                <button 
                  className="carousel-btn" 
                  onClick={nextSlide}
                  disabled={filteredPosts.length <= 3}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
            
            <div className="carousel-container">
              <div className="carousel-posts">
                {getCarouselPosts().map(post => (
                  <div key={post.id} className="carousel-post">
                    <div className="post-header">
                      <div className="post-meta">
                        <Calendar size={14} />
                        <span>{(post.date || '').slice(0, 10)}</span>
                      </div>
                      <div className="post-network">{post.network}</div>
                    </div>
                    
                    <div className="post-author">
                      <User size={14} />
                      <span>{post.author || 'Desconocido'}</span>
                    </div>
                    
                    <div className="post-content">
                      <p className="post-description">
                        {(post.desc || '').length > 120 
                          ? `${post.desc.slice(0, 120)}...` 
                          : post.desc}
                      </p>
                    </div>
                    
                    <div className="post-footer">
                      <div className="post-tags">
                        {post.category && (
                          <span className={`tag tag-${post.category.toLowerCase()}`}>
                            {post.category}
                          </span>
                        )}
                        {post.Estado_Actual && (
                          <span className="tag tag-status">
                            {post.Estado_Actual}
                          </span>
                        )}
                      </div>
                      
                      {post.link && (
                        <a 
                          href={post.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="post-link"
                        >
                          <ExternalLink size={14} />
                          Ver publicación
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Charts */}
        <div ref={dashboardRef}>
          <div className="charts-grid" data-testid="charts-grid">
            <div className="chart-card">
              <h3 className="chart-title">Por Categoría</h3>
              <div className="chart-wrapper">
                <Doughnut data={categoryData} options={chartOptions} />
              </div>
            </div>

            <div className="chart-card">
              <h3 className="chart-title">Por Autor (Top 10)</h3>
              <div className="chart-wrapper">
                <Bar data={authorData} options={barOptions} />
              </div>
            </div>

            <div className="chart-card">
              <h3 className="chart-title">Por Institución (Top 10)</h3>
              <div className="chart-wrapper">
                <Bar data={instData} options={barOptions2} />
              </div>
            </div>

            <div className="chart-card">
              <h3 className="chart-title">Por Red Social</h3>
              <div className="chart-wrapper">
                <Pie data={netData} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
